/**
 * WasteCostOptimizer — Tier-1 Pro tool #4.
 * Pick a recipe, pick optimization goals, and let Claude Sonnet 4.6
 * propose ranked ingredient substitutions with predicted cost / time /
 * salt / fat impact.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Recycle, Sparkles, Loader2, ArrowDown, ArrowUp,
} from 'lucide-react';
import ProTierGate from '../components/tools/ProTierGate';
import PageHero from '../components/PageHero';

const GOALS = [
  ['reduce_cost', 'Lower cost'],
  ['reduce_salt', 'Cut salt'],
  ['reduce_fat',  'Cut fat'],
  ['reduce_time', 'Shorten cycle'],
];

const Delta = ({ value, unit = '', invert = false }) => {
  if (!Number.isFinite(value) || value === 0) return <span className="text-muted-foreground">—</span>;
  const good = invert ? value > 0 : value < 0;
  const color = good ? 'text-emerald-700' : 'text-rose-700';
  const Arrow = value < 0 ? ArrowDown : ArrowUp;
  return (
    <span className={`inline-flex items-center gap-0.5 font-bold ${color}`}>
      <Arrow className="w-3 h-3" />
      {Math.abs(value).toFixed(1)}{unit}
    </span>
  );
};

const Inner = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [recipeId, setRecipeId] = useState('');
  const [goals, setGoals] = useState({ reduce_cost: true, reduce_salt: false, reduce_fat: false, reduce_time: false });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tools/recipe-builder`);
        const list = Array.isArray(data) ? data : [];
        setRecipes(list);
        // Honor `?recipe_id=…` deep link.
        const rid = params.get('recipe_id');
        if (rid && list.find(r => r.id === rid)) setRecipeId(rid);
      } catch { /* silent */ }
    })();
  }, [API, params]);

  const run = async () => {
    if (!recipeId) return toast.error('Pick a recipe');
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/tools/waste-cost-optimizer/analyze`, {
        recipe_id: recipeId,
        goals,
      });
      setResult(data);
      toast.success(`${data.substitutions?.length || 0} suggestions from Claude Sonnet`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Optimizer failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="waste-cost-optimizer-inner">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="wco-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
        <div className="text-[10px] uppercase tracking-widest font-extrabold text-primary">Tier-1 · Pro Tool</div>
      </div>
      <PageHero
        icon={<Recycle className="w-8 h-8" />}
        eyebrow="Waste & Cost Optimizer"
        title="Smarter ingredient swaps"
        subtitle="Pick a saved recipe, tell us what to optimize, and Claude Sonnet 4.6 will rank replacement ingredients with projected impact on cost, macros, and cycle time."
      />

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">1. Recipe + goals</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Recipe</label>
            <select
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
              value={recipeId}
              onChange={(e) => setRecipeId(e.target.value)}
              data-testid="wco-recipe-select"
            >
              <option value="">Pick a saved recipe…</option>
              {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Optimize for</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {GOALS.map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setGoals(g => ({ ...g, [k]: !g[k] }))}
                  className={`px-3 py-1 rounded-full text-xs font-bold border ${goals[k] ? 'bg-primary text-white border-primary' : 'bg-background border-border text-foreground'}`}
                  data-testid={`wco-goal-${k}`}
                >{label}</button>
              ))}
            </div>
          </div>
          <Button onClick={run} disabled={running || !recipeId} className="rounded-full bg-primary text-white font-extrabold" data-testid="wco-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {running ? 'Optimizing…' : 'Run AI optimizer'}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <div className="space-y-4" data-testid="wco-result">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Summary</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-foreground" data-testid="wco-summary">{result.summary}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="rounded-lg p-2 border border-border bg-muted/30">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cost Δ</div>
                  <div className="mt-0.5"><Delta value={result.projected_cost_delta_pct} unit="%" /></div>
                </div>
                <div className="rounded-lg p-2 border border-border bg-muted/30">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cycle Δ</div>
                  <div className="mt-0.5"><Delta value={result.projected_time_delta_hours} unit="h" /></div>
                </div>
                <div className="rounded-lg p-2 border border-border bg-muted/30">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Salt Δ</div>
                  <div className="mt-0.5"><Delta value={result.projected_salt_delta_g} unit="g" /></div>
                </div>
                <div className="rounded-lg p-2 border border-border bg-muted/30">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fat Δ</div>
                  <div className="mt-0.5"><Delta value={result.projected_fat_delta_g} unit="g" /></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Recommended substitutions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {result.substitutions.length === 0 && <div className="text-sm text-muted-foreground italic">Claude didn't find any high-impact swaps.</div>}
              {result.substitutions.map((s, i) => (
                <div key={i} className="rounded-xl border border-border p-3" data-testid={`wco-sub-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-foreground">{s.original_ingredient}</div>
                    <div className="text-muted-foreground">→</div>
                    <div className="font-bold text-primary">{s.replacement_ingredient}</div>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{s.reason}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span>Cost: <Delta value={s.est_cost_delta_pct} unit="%" /></span>
                    <span>Cycle: <Delta value={s.est_time_delta_hours} unit="h" /></span>
                    <span>Salt: <Delta value={s.est_salt_delta_g} unit="g" /></span>
                    <span>Fat: <Delta value={s.est_fat_delta_g} unit="g" /></span>
                  </div>
                  {s.caveats?.length > 0 && (
                    <ul className="mt-2 list-disc list-inside text-[11px] text-muted-foreground">
                      {s.caveats.map((c, j) => <li key={j}>{c}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const WasteCostOptimizer = () => (
  <ProTierGate toolName="Waste & Cost Optimizer" tagline="AI-ranked substitutions that lower cost, cut salts and fats, and speed up cycles." data_testid_prefix="waste-cost-optimizer-gate">
    <Inner />
  </ProTierGate>
);
export default WasteCostOptimizer;
